"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { values } from "src/utils/mock/aboutUsData";

gsap.registerPlugin(ScrollTrigger);

export default function AboutUsCoreValues() {
  const valuesRef = useRef(null);

  useEffect(() => {
    let ctx = gsap.context(() => {
      if (valuesRef.current) {
        const cards = valuesRef.current.querySelectorAll(".value-card");
        gsap.fromTo(
          cards,
          { opacity: 0, y: 70, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.85,
            ease: "power3.out",
            stagger: { amount: 0.4 },
            scrollTrigger: { trigger: valuesRef.current, start: "top 80%" },
          },
        );
      }
    });

    return () => ctx?.revert();
  }, []);

  return (
    <>
      <div className="footer-top-accent" aria-hidden="true" />
      <section className="py-10! bg-background-primary relative overflow-hidden">
        <div className="container max-w-[1400px] mx-auto! px-6! relative z-10">
          <div className="text-center mb-16! flex flex-col items-center">
            <div className="flex items-center gap-4 mb-4!">
              <div className="w-12 h-px bg-purple-500/50"></div>
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-500 dark:to-blue-500">
                Our Core Values
              </p>
              <div className="w-12 h-px bg-blue-500/50"></div>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium text-text-primary tracking-tight">
              The{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-500 dark:to-blue-400">
                principles
              </span>{" "}
              that drive everything we do.
            </h2>
          </div>

          <div
            ref={valuesRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-6"
          >
            {values.map((v, i) => {
              const styles = [
                {
                  border: "border-purple-500/30 hover:border-purple-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(168,85,247,0.15)]",
                  glow: "text-purple-400 shadow-[inset_0_0_20px_rgba(168,85,247,0.3)] border border-purple-500/30",
                  bar: "bg-purple-500",
                },
                {
                  border: "border-blue-500/30 hover:border-blue-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(59,130,246,0.15)]",
                  glow: "text-blue-400 shadow-[inset_0_0_20px_rgba(59,130,246,0.3)] border border-blue-500/30",
                  bar: "bg-blue-500",
                },
                {
                  border: "border-emerald-500/30 hover:border-emerald-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(16,185,129,0.15)]",
                  glow: "text-emerald-400 shadow-[inset_0_0_20px_rgba(16,185,129,0.3)] border border-emerald-500/30",
                  bar: "bg-emerald-500",
                },
                {
                  border: "border-pink-500/30 hover:border-pink-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(236,72,153,0.15)]",
                  glow: "text-pink-400 shadow-[inset_0_0_20px_rgba(236,72,153,0.3)] border border-pink-500/30",
                  bar: "bg-pink-500",
                },
                {
                  border: "border-orange-500/30 hover:border-orange-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(249,115,22,0.15)]",
                  glow: "text-orange-400 shadow-[inset_0_0_20px_rgba(249,115,22,0.3)] border border-orange-500/30",
                  bar: "bg-orange-500",
                },
                {
                  border: "border-fuchsia-500/30 hover:border-fuchsia-500/80",
                  shadow: "hover:shadow-[0_0_25px_rgba(217,70,239,0.15)]",
                  glow: "text-fuchsia-400 shadow-[inset_0_0_20px_rgba(217,70,239,0.3)] border border-fuchsia-500/30",
                  bar: "bg-fuchsia-500",
                },
              ][i % 6];

              return (
                <div
                  key={v.title}
                  className={`value-card group p-6! rounded-2xl border ${styles.border} transition-all duration-500 ${styles.shadow} flex flex-col items-center text-center relative overflow-hidden cursor-default`}
                  style={{
                    background: "var(--card-background)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center mb-6! group-hover:scale-110 transition-transform duration-500 relative z-10 ${styles.glow} bg-surface-secondary`}
                  >
                    <v.icon className="w-7 h-7" />
                  </div>

                  <h3 className="font-display font-semibold text-lg text-text-primary mb-4! relative z-10">
                    {v.title}
                  </h3>
                  <p className="text-text-secondary text-[13px] leading-relaxed mb-6! flex-1 relative z-10">
                    {v.desc}
                  </p>

                  <div
                    className={`w-6 h-1 rounded-full ${styles.bar} mt-auto opacity-70 group-hover:w-10 group-hover:opacity-100 transition-all duration-500`}
                  ></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
